import type { EventBus } from "@earendil-works/pi-coding-agent";
import type { TodoRuntime } from "./runtime.ts";
import { TodoValidationError } from "./scheduler.ts";
import type { AgentLifecycleEvent, TodoAgentMetadata } from "./types.ts";

export const AGENT_LIFECYCLE_CHANNEL = "pi:agent:lifecycle";
const PROTOCOL_STATE_LIMIT = 2000;

export function registerAgentLifecycleProtocol(events: EventBus, runtime: TodoRuntime): () => void {
	const seen = new Set<string>();
	const terminalRuns = new Set<string>();
	const currentRuns = new Map<string, { agentId: string; runId: string; timestamp: string }>();
	const operations = new Map<string, Promise<void>>();
	return events.on(AGENT_LIFECYCLE_CHANNEL, async (data) => {
		const event = parseLifecycleEvent(data);
		if (!event) return;
		const metadata = parseTodoMetadata(event.metadata);
		if (!metadata || metadata["pi.todo/list-id"] !== runtime.getListId()) return;
		const taskKey = `${metadata["pi.todo/list-id"]}\0${metadata["pi.todo/task-id"]}`;
		const runKey = `${taskKey}\0${event.agentId}\0${event.runId}`;
		const previous = operations.get(taskKey) ?? Promise.resolve();
		const operation = previous
			.catch(() => {})
			.then(async () => {
				if (seen.has(event.eventId)) return;
				try {
					const current = currentRuns.get(taskKey);
					if (isActiveStatus(event.status)) {
						if (terminalRuns.has(runKey)) return;
						if (
							current &&
							(current.agentId !== event.agentId || current.runId !== event.runId) &&
							current.timestamp > event.timestamp
						)
							return;
						await runtime.confirmOwnerLive(metadata["pi.todo/task-id"], event.agentId);
						currentRuns.set(taskKey, { agentId: event.agentId, runId: event.runId, timestamp: event.timestamp });
					} else {
						if (current && (current.agentId !== event.agentId || current.runId !== event.runId)) return;
						if (event.status === "completed") {
							const task = await runtime.getTask(metadata["pi.todo/task-id"]);
							if (task?.status !== "completed") {
								// Surface the mismatch to the model instead of silently swallowing it:
								// the pending entry is rendered in the next digest so the coordinator
								// can check the task off with todo_update after verifying.
								runtime.recordPendingReconcile(metadata["pi.todo/task-id"], event.agentId);
							} else {
								await runtime.syncCurrent();
							}
						} else {
							await runtime.releaseIfOwned(metadata["pi.todo/task-id"], event.agentId);
						}
						remember(terminalRuns, runKey);
					}
					remember(seen, event.eventId);
				} catch (error) {
					if (!(error instanceof TodoValidationError)) throw error;
				}
			});
		operations.set(taskKey, operation);
		try {
			await operation;
		} finally {
			if (operations.get(taskKey) === operation) operations.delete(taskKey);
		}
	});
}

function remember(values: Set<string>, value: string): void {
	values.add(value);
	if (values.size > PROTOCOL_STATE_LIMIT) values.delete(values.values().next().value ?? "");
}

function isActiveStatus(status: AgentLifecycleEvent["status"]): boolean {
	return status === "queued" || status === "started" || status === "running";
}

function parseLifecycleEvent(value: unknown): AgentLifecycleEvent | undefined {
	if (typeof value !== "object" || value === null) return undefined;
	const record = value as Record<string, unknown>;
	if (
		record.version !== 2 ||
		typeof record.eventId !== "string" ||
		typeof record.runId !== "string" ||
		typeof record.agentId !== "string" ||
		typeof record.parentSessionId !== "string" ||
		typeof record.timestamp !== "string" ||
		typeof record.metadata !== "object" ||
		record.metadata === null ||
		!isLifecycleStatus(record.status)
	) {
		return undefined;
	}
	return record as unknown as AgentLifecycleEvent;
}

function parseTodoMetadata(metadata: Record<string, unknown>): TodoAgentMetadata | undefined {
	const listId = metadata["pi.todo/list-id"];
	const taskId = metadata["pi.todo/task-id"];
	if (typeof listId !== "string" || typeof taskId !== "string") return undefined;
	return { "pi.todo/list-id": listId, "pi.todo/task-id": taskId };
}

function isLifecycleStatus(value: unknown): value is AgentLifecycleEvent["status"] {
	return ["queued", "started", "running", "completed", "failed", "stopped", "interrupted"].includes(String(value));
}
